import React, { useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { MobileModal } from "./MobileModal";

interface MobileDatePickerProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  value?: string | Dayjs; // Accepts 'YYYY-MM-DD' or dayjs object
  onChange: (date: string) => void;
  footerText?: string;
}

const WEEKS = ["日", "一", "二", "三", "四", "五", "六"];

export const MobileDatePicker: React.FC<MobileDatePickerProps> = ({ visible, onClose, title = "選擇日期", value, onChange, footerText = "請滑動或點擊選擇精準日期" }) => {
  // State for the currently viewed month/year
  const [viewDate, setViewDate] = useState<Dayjs>(dayjs());
  // State for the selected date
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  // Initialize state when opening or value changing
  useEffect(() => {
    if (visible) {
      if (value) {
        const d = dayjs(value);
        if (d.isValid()) {
          setSelectedDate(d);
          setViewDate(d);
        } else {
          setSelectedDate(null);
          setViewDate(dayjs());
        }
      } else {
        setSelectedDate(null);
        setViewDate(dayjs());
      }
    }
  }, [visible, value]);

  const handleYearChange = (year: number) => {
    setViewDate(viewDate.year(year));
  };

  const handleMonthChange = (month: number) => {
    setViewDate(viewDate.month(month - 1)); // Antd Select 1-12, dayjs 0-11
  };

  const handleDateClick = (date: Dayjs) => {
    setSelectedDate(date);
    onChange(date.format("YYYY-MM-DD"));
    console.log(date.format("YYYY-MM-DD"));
    onClose();
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const startOfMonth = viewDate.startOf("month");
    const startDayOfWeek = startOfMonth.day(); // 0 (Sunday) to 6 (Saturday)
    const daysInMonth = viewDate.daysInMonth();

    const days = [];

    // Empty slots for previous month
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ type: "empty", key: `empty-${i}` });
    }

    // Days of current month
    for (let i = 1; i <= daysInMonth; i++) {
      const date = viewDate.date(i);
      days.push({ type: "day", date, key: date.format("YYYY-MM-DD") });
    }

    return days;
  }, [viewDate]);

  // Year options (e.g., 1900 - Current)
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const years = [];
    for (let y = currentYear - 100; y <= currentYear; y++) {
      years.push({ label: `${y}年`, value: y });
    }
    return years; // Ascending order
  }, []);

  // Month options
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      label: `${i + 1}月`,
      value: i + 1,
    }));
  }, []);

  return (
    <MobileModal visible={visible} onClose={onClose} title={title} footerText={footerText}>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {/* Year/Month Selectors */}
        <div style={{ display: "flex", gap: "16px" }}>
          <Select
            size="large"
            value={viewDate.year()}
            onChange={handleYearChange}
            options={yearOptions}
            style={{ flex: 1, height: "44px" }}
            popupMatchSelectWidth={false}
            className="mobile-picker-select" // For global styling overrides if needed
            dropdownStyle={{ zIndex: 100005 }}
          />
          <Select
            size="large"
            value={viewDate.month() + 1}
            onChange={handleMonthChange}
            options={monthOptions}
            style={{ flex: 1, height: "44px" }}
            popupMatchSelectWidth={false}
            className="mobile-picker-select"
            dropdownStyle={{ zIndex: 100005 }}
          />
        </div>

        {/* Calendar Grid */}
        <div style={{ width: "100%" }}>
          {/* Week Headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: "12px" }}>
            {WEEKS.map((w) => (
              <div key={w} style={{ textAlign: "center", color: "#999", fontSize: "14px", fontWeight: 500 }}>
                {w}
              </div>
            ))}
          </div>

          {/* Days */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "16px" }}>
            {calendarDays.map((item: any) => {
              if (item.type === "empty") {
                return <div key={item.key} />;
              }

              const isSelected = selectedDate && item.date.isSame(selectedDate, "day");
              const isToday = item.date.isSame(dayjs(), "day");

              return (
                <div
                  key={item.key}
                  onClick={() => handleDateClick(item.date)}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "16px",
                      color: isSelected ? "#fff" : "#333",
                      backgroundColor: isSelected ? "#00C7C7" : "transparent",
                      fontWeight: isSelected || isToday ? 600 : 400,
                      border: isToday && !isSelected ? "1px solid #00C7C7" : "none",
                    }}
                  >
                    {item.date.date()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </MobileModal>
  );
};
